import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { FieldLabel } from './ui/Field'
import { api, extractApiError } from '../lib/api'
import { folderListResponse, spaceListResponse } from '../lib/types'

const ROOT_FOLDER_VALUE = '__root__'

export function ListMoveDialog({ open, list, workspaceId, onClose }) {
  const qc = useQueryClient()
  const [targetSpaceId, setTargetSpaceId] = useState(null)
  const [targetFolderId, setTargetFolderId] = useState(null)

  const spacesQuery = useQuery({
    queryKey: ['spaces', workspaceId],
    enabled: open && Boolean(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return { data: [] }
      const res = await api.get(`/workspaces/${workspaceId}/spaces`)
      return spaceListResponse.parse(res.data)
    },
  })
  const spaces = (spacesQuery.data?.data ?? []).filter((s) => !s.isArchived)

  const foldersQuery = useQuery({
    queryKey: ['folders', targetSpaceId],
    enabled: open && Boolean(targetSpaceId),
    queryFn: async () => {
      if (!targetSpaceId) return { data: [] }
      const res = await api.get(`/spaces/${targetSpaceId}/folders`)
      return folderListResponse.parse(res.data)
    },
  })
  const folders = foldersQuery.data?.data ?? []

  useEffect(() => {
    if (open) {
      const initialSpace = spaces[0]?.id ?? null
      setTargetSpaceId(list?.spaceId ?? initialSpace)
      setTargetFolderId(list?.folderId ?? null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, list?.id])

  useEffect(() => {
    if (!open) return
    if (!targetFolderId) return
    if (folders.some((f) => f.id === targetFolderId)) return
    setTargetFolderId(null)
  }, [open, targetSpaceId, folders, targetFolderId])

  const moveMutation = useMutation({
    mutationFn: async () => {
      if (!list) throw new Error('No list selected.')
      if (!targetSpaceId) throw new Error('Pick a destination Space.')
      await api.patch(`/spaces/${list.spaceId}/lists/${list.id}`, {
        spaceId: targetSpaceId,
        folderId: targetFolderId,
      })
      return { fromSpaceId: list.spaceId, toSpaceId: targetSpaceId }
    },
    onSuccess: ({ fromSpaceId, toSpaceId }) => {
      toast.success('List moved successfully.')
      onClose()
      void qc.invalidateQueries({ queryKey: ['lists', fromSpaceId] })
      void qc.invalidateQueries({ queryKey: ['lists', toSpaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const noChange =
    list &&
    targetSpaceId === list.spaceId &&
    (targetFolderId ?? null) === (list.folderId ?? null)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move To"
      description={list ? `Move "${list.name}" to a different Space or Project.` : ''}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={moveMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => moveMutation.mutate()}
            loading={moveMutation.isPending}
            disabled={!targetSpaceId || spaces.length === 0 || Boolean(noChange)}
          >
            Move
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <FieldLabel label="Destination Space" htmlFor="list-move-space" />
          <select
            id="list-move-space"
            value={targetSpaceId ?? ''}
            onChange={(e) => setTargetSpaceId(e.target.value || null)}
            disabled={spacesQuery.isLoading || spaces.length === 0}
            className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-60"
          >
            <option value="" disabled hidden>
              {spacesQuery.isLoading
                ? 'Loading spaces…'
                : spaces.length === 0
                ? 'No Spaces available'
                : 'Pick a Space'}
            </option>
            {spaces.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <FieldLabel label="Destination Project (Optional)" htmlFor="list-move-folder" />
          <select
            id="list-move-folder"
            value={targetFolderId ?? ROOT_FOLDER_VALUE}
            onChange={(e) =>
              setTargetFolderId(e.target.value === ROOT_FOLDER_VALUE ? null : e.target.value)
            }
            disabled={!targetSpaceId || foldersQuery.isLoading}
            className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-60"
          >
            <option value={ROOT_FOLDER_VALUE}>(No project — at Space root)</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  )
}
